import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount, usePartnerAccess } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import paymentporterService from '../services/paymentporterService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import vaultPaymentService from '../services/vaultPaymentService';
import hotelService from '../services/hotelService';
import WizardPaymentStep from './WizardPaymentStep';
import useCredentialStep from './useCredentialStep';
import WizardStepper from './WizardStepper';
import WizardHotelStep from './WizardHotelStep';
import WizardPmsStep from './WizardPmsStep';
import WizardCredentialsStep from './WizardCredentialsStep';
import WizardMarkerStep from './WizardMarkerStep';
import NotificationsPanel from './shared/NotificationsPanel';
import './AddPaymentPorterWizard.css';

const STEPS = [
    { num: 1, label: 'Name' },
    { num: 2, label: 'Hotel' },
    { num: 3, label: 'PMS' },
    { num: 4, label: 'Credentials' },
    { num: 5, label: 'Notifications' },
    { num: 6, label: 'Marker' },
    { num: 7, label: 'Payment' },
];

const AddPaymentPorterWizard = () => {
    const navigate = useNavigate();
    const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();
    const { isSuperAdmin, isPartner, solePartnerId, user } = useAuth();
    const { stripeReady, partnerDataLoaded } = usePartnerAccess(isSuperAdmin);
    const invoicePrivilege = (isSuperAdmin || isPartner) ? 'partner' : isChainAdmin ? 'chain' : 'hotel';

    const [step, setStep] = useState(1);
    const [form, setForm] = useState({ name: '', description: '' });
    const [errors, setErrors] = useState({});

    const [hotels, setHotels]               = useState([]);
    const [hotelsLoading, setHotelsLoading] = useState(false);
    const [selectedHotel, setSelectedHotel] = useState(null);

    const [setupData, setSetupData]       = useState(null);
    const [setupLoading, setSetupLoading] = useState(false);

    const [selectedMarketplace, setSelectedMarketplace] = useState(null);

    const cs = useCredentialStep({
        selectedMarketplace,
        selectedHotel,
        selectedAccount,
        selectedPartner,
        solePartnerId,
        isSuperAdmin,
        isPartner,
        isChainAdmin,
    });

    // Notifications step — managed by the shared NotificationsPanel (wizard mode: collected
    // here, persisted on create). { emails: [{email, source}], teamsWebhook: string|null }
    const [notif, setNotif] = useState({ emails: [], teamsWebhook: null });

    const [udfField, setUdfField] = useState('UDFC01');

    const [saving, setSaving] = useState(false);
    const [awaitingPayment, setAwaitingPayment] = useState(false);
    const [invoiceResult, setInvoiceResult] = useState(null);
    const [createdPpId, setCreatedPpId] = useState(null);

    // Step 1 → Step 2 (Hotel)
    const goToStep2 = async () => {
        if (!form.name.trim()) { setErrors({ name: 'Name is required' }); return; }
        setErrors({});
        setHotelsLoading(true);
        try {
            // Scope to the selected chain, or to the selected partner's hotels when "All Hotel Chains"
            // is selected — otherwise other partners' hotels would appear (HP-75).
            const res = await hotelService.getAllHotels(
                selectedAccount ? { accountid: selectedAccount } : { partnerid: selectedPartner || solePartnerId }
            );
            setHotels(res.hotels || []);
        } catch (err) {
            console.error('Failed to load hotels', err);
        } finally {
            setHotelsLoading(false);
        }
        setStep(2);
    };

    // Step 2 → Step 3 (PMS)
    const goToStep3 = async () => {
        if (!selectedHotel) { setErrors({ hotel: 'Please select a hotel' }); return; }
        setErrors({});
        setSetupLoading(true);
        try {
            const credFetchers = [entityMktSettingsService.listCredentials('hotel', selectedHotel.hotelid)];
            if (selectedAccount) credFetchers.push(entityMktSettingsService.listCredentials('account', selectedAccount));
            const effectivePartnerId = selectedPartner || solePartnerId;
            if (effectivePartnerId) credFetchers.push(entityMktSettingsService.listCredentials('partner', effectivePartnerId));

            const credEntityTypes = ['hotel'];
            if (selectedAccount) credEntityTypes.push('account');
            if (effectivePartnerId) credEntityTypes.push('partner');

            const [setupRes, ...credResults] = await Promise.all([
                paymentporterService.getSetupData(selectedHotel.hotelid, selectedAccount),
                ...credFetchers,
            ]);
            setSetupData(setupRes.data);
            const merged = credResults.flatMap((r, i) =>
                (r.credentials || []).map(c => ({ ...c, _entityType: credEntityTypes[i] }))
            );
            cs.setCredentials(merged);
        } catch (err) {
            console.error('Failed to load setup data', err);
        } finally {
            setSetupLoading(false);
        }
        setStep(3);
    };

    // Step 3 → Step 4 (Credentials)
    const goToStep4 = async () => {
        if (!selectedMarketplace) { setErrors({ marketplace: 'Please select a marketplace' }); return; }
        setErrors({});
        await cs.loadForMarketplace();
        setStep(4);
    };

    // Step 4 → Step 5 (Notifications) — defaults are fetched by the NotificationsPanel itself
    const goToStep5 = async () => {
        const err = cs.validate();
        if (err) { setErrors(err); return; }
        setErrors({});
        setStep(5);
    };

    // Step 5 → Step 6 (Marker)
    const goToStep6 = () => { setErrors({}); setStep(6); };

    const buildPPPaymentService = () => ({
        resolve: async () => {
            const [setupRes, vaultRes] = await Promise.all([
                paymentporterService.getSetupData(selectedHotel?.hotelid, selectedAccount),
                vaultPaymentService.resolve(selectedHotel?.hotelid, selectedAccount),
            ]);
            const sd = setupRes.data;
            const p  = sd.pricing || {};
            const prices = [
                p.monthly && { id: p.monthly.price_id, amount: p.monthly.unit_amount / 100, currency: p.monthly.currency, recurring: 'month', lookup_key: p.monthly.lookup_key },
                p.annual  && { id: p.annual.price_id,  amount: p.annual.unit_amount  / 100, currency: p.annual.currency,  recurring: 'year',  lookup_key: p.annual.lookup_key  },
            ].filter(Boolean);
            const hotelSeats  = sd.hotelSeats?.free_seats  ?? 0;
            const chainSeats  = sd.accountSeats?.free_seats ?? 0;
            return {
                data: {
                    prices,
                    upfrontPrice:    p.upfront ? { price_id: p.upfront.price_id, unit_amount: p.upfront.unit_amount, currency: p.upfront.currency } : null,
                    paymentMethods:  vaultRes.data?.paymentMethods  || [],
                    billingContacts: vaultRes.data?.billingContacts  || null,
                    freeSeats:       hotelSeats + chainSeats,
                    freeSeatLevel:   hotelSeats > 0 ? 'hotel' : (chainSeats > 0 ? 'chain' : null),
                },
            };
        },
        createSetupIntent: (acctId, acctName, hId) => vaultPaymentService.createSetupIntent(acctId, acctName, hId),
        // createSubscription and useFreeSeat intentionally omitted — PP creates the project after payment
    });

    const buildPpPayload = () => ({
        name:                       form.name.trim(),
        description:                form.description.trim() || null,
        account_id:                 selectedAccount,
        hotel_id:                   selectedHotel?.hotelid || null,
        marketplace_id:             selectedMarketplace?.id || null,
        ...cs.buildCredentialPayload(),
        hotelid_override:           cs.hotelIdOverride.trim() || null,
        udf_field:                  udfField,
        notification_emails:        notif.emails.map(({ email, source }) => ({ email, source })),
        notification_teams_webhook: notif.teamsWebhook || null,
    });

    // Superadmin override — bypass Stripe and create the Payment Porter as paid + active.
    const handleMarkPaid = async () => {
        if (saving) return;
        setSaving(true);
        setErrors({});
        try {
            const res = await paymentporterService.create({ ...buildPpPayload(), active: true, use_free_seat: false, paid: 'Y' });
            const id = res.data?.id;
            navigate(id ? `/apps/payment-porter/${id}` : '/apps/payment-porter');
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || err.message || 'Failed to mark paid' });
            setSaving(false);
        }
    };

    const handlePaymentComplete = async (result) => {
        if (saving) return;
        setSaving(true);
        setErrors({});
        const ppPayload = buildPpPayload();

        try {
            if (result.type === 'free-seat') {
                const res = await paymentporterService.create({ ...ppPayload, active: true, use_free_seat: true, paid: 'Y' });
                const id = res.data?.id;
                navigate(id ? `/apps/payment-porter/${id}` : '/apps/payment-porter');
                return;
            }

            if (result.type === 'subscription') {
                const subRes = await vaultPaymentService.createSubscription({
                    accountId:       selectedAccount,
                    hotelId:         selectedHotel?.hotelid || null,   // same payer the card was saved on
                    paymentMethodId: result.paymentMethodId,
                    stripePriceId:   result.stripePriceId,
                });
                const res = await paymentporterService.create({
                    ...ppPayload,
                    active:                 true,
                    use_free_seat:          false,
                    paid:                   'Y',
                    stripe_subscription_id: subRes.data?.subscriptionId,
                });
                const id = res.data?.id;
                navigate(id ? `/apps/payment-porter/${id}` : '/apps/payment-porter');
                return;
            }

            if (result.type === 'invoice') {
                // Create PP first (inactive until payment confirmed)
                let ppId = createdPpId;
                if (!ppId) {
                    const createRes = await paymentporterService.create({ ...ppPayload, active: false, use_free_seat: false, paid: 'N' });
                    ppId = createRes.data?.id;
                    if (ppId) setCreatedPpId(ppId);
                }
                if (!ppId) throw new Error('Failed to create Payment Porter');

                const invRes = await paymentporterService.createInvoice({
                    ppId,
                    accountId:     selectedAccount,
                    hotelId:       selectedHotel?.hotelid,
                    stripePriceId: result.stripePriceId,
                    targetType:    result.invoiceTarget,
                    customerEmail: result.invoiceTarget === 'me' ? result.contactEmail : undefined,
                });
                setInvoiceResult(invRes?.data || null);
                setAwaitingPayment(true);
                setSaving(false);
                return;
            }
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || err.message || 'Failed to complete payment' });
            setSaving(false);
        }
    };

    if (partnerDataLoaded && !stripeReady) {
        return (
            <div className="ppw-page">
                <div className="ppw-card">
                    <div className="ppw-card-body">
                        <div className="ppw-error-msg" style={{ padding: '1rem' }}>
                            Your partner account must have Stripe connected before you can create Payment Porter projects. Go to Partner Settings → Stripe to complete onboarding.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ppw-page">
            <WizardStepper steps={STEPS} currentStep={step} />

            {/* ── Step 1: Name ─────────────────────────────────────────────── */}
            {step === 1 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Name your Payment Porter</h2>
                        <p className="ppw-card-subtitle">Give this configuration a name</p>
                    </div>
                    <div className="ppw-card-body">
                        <div className="ppw-field">
                            <label className="ppw-label">Name <span className="ppw-required">*</span></label>
                            <input
                                className={`ppw-input${errors.name ? ' ppw-input-error' : ''}`}
                                type="text"
                                placeholder="e.g., Main Hotel Payment Porter"
                                value={form.name}
                                onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors(v => ({ ...v, name: undefined })); }}
                                autoFocus
                            />
                            {errors.name && <span className="ppw-error-msg">{errors.name}</span>}
                        </div>
                        <div className="ppw-field">
                            <label className="ppw-label">Description</label>
                            <textarea
                                className="ppw-input"
                                rows={3}
                                placeholder="Optional description"
                                value={form.description}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-cancel" onClick={() => navigate('/apps/payment-porter')}>Cancel</button>
                        <button className="ppw-btn-next" onClick={goToStep2} disabled={hotelsLoading}>
                            {hotelsLoading ? 'Loading...' : 'Next →'}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Step 2: Hotel ─────────────────────────────────────────────── */}
            {step === 2 && (
                <WizardHotelStep
                    hotels={hotels}
                    selectedHotel={selectedHotel}
                    onSelect={h => { setSelectedHotel(h); setErrors(v => ({ ...v, hotel: undefined })); }}
                    error={errors.hotel}
                    subtitle="Choose the hotel this Payment Porter is for"
                    onBack={() => setStep(1)}
                    onNext={goToStep3}
                    loading={setupLoading}
                />
            )}

            {/* ── Step 3: PMS ──────────────────────────────────────────────── */}
            {step === 3 && (
                <WizardPmsStep
                    marketplaces={setupData?.marketplaces || []}
                    selectedMarketplace={selectedMarketplace}
                    onSelect={m => { setSelectedMarketplace(m); setErrors(v => ({ ...v, marketplace: undefined })); }}
                    error={errors.marketplace}
                    subtitle="Choose the system this Payment Porter will connect to"
                    onBack={() => setStep(2)}
                    onNext={goToStep4}
                />
            )}

            {/* ── Step 4: Credentials ──────────────────────────────────────── */}
            {step === 4 && (
                <WizardCredentialsStep
                    selectedMarketplace={selectedMarketplace}
                    selectedHotel={selectedHotel}
                    accountId={selectedAccount}
                    partnerId={selectedPartner || solePartnerId}
                    cs={cs}
                    errors={errors}
                    onClearError={key => setErrors(v => ({ ...v, [key]: undefined }))}
                    onBack={() => { setStep(3); cs.setShowAddCredForm(false); }}
                    onNext={goToStep5}
                />
            )}

            {/* ── Step 5: Notifications ────────────────────────────────────── */}
            {step === 5 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Notifications</h2>
                        <p className="ppw-card-subtitle">Choose who receives error alerts and a daily summary of ported payments.</p>
                    </div>
                    <div className="ppw-card-body">
                        <NotificationsPanel
                            value={notif}
                            onChange={setNotif}
                            hotelId={selectedHotel?.hotelid}
                            accountId={selectedAccount}
                        />
                    </div>
                    <div className="ppw-footer">
                        <button className="ppw-btn-back" onClick={() => setStep(4)}>← Back</button>
                        <button className="ppw-btn-next" onClick={goToStep6}>Next →</button>
                    </div>
                </div>
            )}

            {/* ── Step 6: Marker (UDF) ─────────────────────────────────────── */}
            {step === 6 && (
                <WizardMarkerStep
                    udfField={udfField}
                    onChange={setUdfField}
                    subtitle="OHIP user-defined character field where this porter records its PP:<timestamp> marker so the same reservation isn't processed twice."
                    onBack={() => setStep(5)}
                    onNext={() => { setErrors({}); setStep(7); }}
                />
            )}

            {/* ── Step 7: Payment ──────────────────────────────────────────── */}
            {step === 7 && (
                <WizardPaymentStep
                    subtitle="A payment is required to activate this Payment Porter."
                    description="Choose how you would like to activate this Payment Porter."
                    awaiting={awaitingPayment}
                    invoiceResult={invoiceResult}
                    awaitingMessage="Your invoice has been emailed. The Payment Porter will be activated once Stripe confirms the payment."
                    saving={saving}
                    error={errors.submit}
                    hotelId={selectedHotel?.hotelid}
                    accountId={selectedAccount}
                    invoicePrivilege={invoicePrivilege}
                    currentUser={user}
                    isSuperAdmin={isSuperAdmin}
                    paymentService={buildPPPaymentService()}
                    onSuccess={handlePaymentComplete}
                    onMarkPaid={handleMarkPaid}
                    markPaidMessage="Mark this Payment Porter as paid manually? This bypasses Stripe and immediately activates it."
                    onBack={() => setStep(6)}
                />
            )}
        </div>
    );
};

export default AddPaymentPorterWizard;
